import { safeErrorDetails } from "../utils/logDiagnostics";
import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";
import { apiClient } from "../api/axios";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";

let initialized = false;
let listenersBound = false;
let lastPushToken = null;
let navigateHandler = null;

const PUSH_HISTORY_KEY = "stirup_notifications_v1";
const PUSH_HISTORY_MAX = 100;

/* ===============================
   NAVIGATION BRIDGE
================================ */
export function setPushNavigationHandler(handler) {
  navigateHandler = typeof handler === "function" ? handler : null;
}

/* ===============================
   HELPERS
================================ */
function isValidFcmToken(token) {
  return typeof token === "string" && token.trim().length > 0;
}

function safeJsonParse(raw, fallback) {
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function readPushHistory() {
  const raw = localStorage.getItem(PUSH_HISTORY_KEY);
  const list = safeJsonParse(raw || "[]", []);
  return Array.isArray(list) ? list : [];
}

function writePushHistory(items) {
  localStorage.setItem(PUSH_HISTORY_KEY, JSON.stringify(items));
}

function normalizePushData(input = {}) {
  const data = input && typeof input === "object" ? input : {};

  const type = String(data.type || "").trim();
  const target = String(data.target || "").trim();
  const orderId = String(data.orderId || data.bookingId || "").trim();
  const promoId = String(data.promoId || "").trim();
  const status = String(data.status || "").trim();
  const promoCode = String(data.promoCode || "").trim();
  const clickAction = String(data.clickAction || "").trim();

  return {
    type,
    target,
    orderId,
    promoId,
    status,
    promoCode,
    clickAction,
    source: String(data.source || "system").trim(),
    raw: data,
  };
}

function saveNotificationToHistory({ title, body, data, origin = "push" }) {
  const normalized = normalizePushData(data);

  const entry = {
    id: `push_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    title: String(title || "Stirup"),
    body: String(body || ""),
    type: normalized.type,
    target: normalized.target,
    orderId: normalized.orderId,
    promoId: normalized.promoId,
    status: normalized.status,
    promoCode: normalized.promoCode,
    source: normalized.source,
    origin,
    receivedAt: new Date().toISOString(),
    readAt: null,
  };

  const current = readPushHistory();
  const next = [entry, ...current].slice(0, PUSH_HISTORY_MAX);
  writePushHistory(next);

  return entry;
}

export function getPushHistory() {
  return readPushHistory();
}

export function markPushAsRead(notificationId) {
  const current = readPushHistory();
  const next = current.map((item) =>
    item.id === notificationId && !item.readAt
      ? { ...item, readAt: new Date().toISOString() }
      : item
  );
  writePushHistory(next);
}

export function clearPushHistory() {
  localStorage.removeItem(PUSH_HISTORY_KEY);
}

function getRoleFromToken() {
  try {
    const token = localStorage.getItem("token");
    if (!token) return "";
    const base64 = token.split(".")[1];
    const payload = JSON.parse(
      atob(base64.replace(/-/g, "+").replace(/_/g, "/"))
    );
    return String(payload?.role || "").toLowerCase();
  } catch {
    return "";
  }
}

function resolvePushRoute(inputData = {}) {
  const data = normalizePushData(inputData);
  const role = getRoleFromToken();

  switch (data.type) {
    case "order_status":
    case "booking_created":
      return data.orderId ? `/ordini/${data.orderId}` : "/ordini";

    case "provider_booking_created":
      return data.orderId
        ? `/fornitore/ordini/${data.orderId}`
        : "/fornitore/ordini";

    case "promo_assigned":
      return "/profilo";

    default:
      if (data.target === "supplier_order_detail") {
        return data.orderId
          ? `/fornitore/ordini/${data.orderId}`
          : "/fornitore/ordini";
      }

      if (data.target === "order_detail") {
        return data.orderId ? `/ordini/${data.orderId}` : "/ordini";
      }

      if (data.target === "profilo_promozioni") {
        return "/profilo";
      }

      return role === "fornitore" ? "/fornitore/ordini" : "/dashboard";
  }
}

function handlePushNavigation(data) {
  const route = resolvePushRoute(data);

  if (!navigateHandler) {
    console.warn("[PUSH] navigateHandler non pronto");
    return;
  }

  navigateHandler(route);
}

async function registerTokenIfPossible(tokenValue = lastPushToken) {
  const jwt = localStorage.getItem("token");
  const token = typeof tokenValue === "string" ? tokenValue.trim() : "";

  if (!jwt || !token) {
    if (!jwt) {
      console.warn("Nessun JWT trovato, skip registrazione push");
    }
    if (!token) {
      console.warn("Nessun token push valido, skip registrazione push");
    }
    return;
  }

  try {
    await apiClient.post(
      "/api/push/register",
      {
        token,
        platform: Capacitor.getPlatform(),
      },
      {
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      }
    );

    lastPushToken = token;
    console.log("✅ Token push salvato a backend");
  } catch (err) {
    console.error("❌ Errore invio token push al server:", safeErrorDetails(err));
  }
}

async function getAndStoreFcmToken() {
  try {
    const result = await FirebaseMessaging.getToken();
    const token = result?.token?.trim?.() || "";

    console.log("[PUSH] Token FCM ricevuto");

    if (!isValidFcmToken(token)) {
      console.warn("Token FCM non valido o vuoto");
      return null;
    }

    lastPushToken = token;
    await registerTokenIfPossible(token);
    return token;
  } catch (err) {
    console.error("❌ Errore recupero token FCM:", safeErrorDetails(err));
    return null;
  }
}

/* ===============================
   LISTENERS
================================ */
function bindPushListeners() {
  FirebaseMessaging.addListener("tokenReceived", async (event) => {
    const token = event?.token?.trim?.() || "";
    console.log("[PUSH] Token FCM aggiornato");

    if (!isValidFcmToken(token)) {
      console.warn("tokenReceived senza token valido");
      return;
    }

    lastPushToken = token;
    await registerTokenIfPossible(token);
  });

  FirebaseMessaging.addListener("notificationReceived", async (event) => {
    console.log("[PUSH] Notifica foreground ricevuta");

    const notification = event?.notification || {};
    const title = notification.title || "Stirup";
    const body = notification.body || "";
    const data = notification.data || {};

    saveNotificationToHistory({
      title,
      body,
      data,
      origin: "foreground_push",
    });

    try {
      await LocalNotifications.schedule({
        notifications: [
          {
            id: Date.now(),
            title,
            body,
            extra: data,
            schedule: {
              at: new Date(Date.now() + 200),
            },
          },
        ],
      });

      console.log("[LOCAL] Notifica locale schedulata con successo");
    } catch (err) {
      console.error("[LOCAL] Errore LocalNotifications.schedule:", safeErrorDetails(err));
    }
  });

  FirebaseMessaging.addListener("notificationActionPerformed", (event) => {
    console.log("[PUSH] Notifica aperta");

    const notification = event?.notification || {};
    const title = notification.title || "Stirup";
    const body = notification.body || "";
    const data = notification.data || {};

    saveNotificationToHistory({
      title,
      body,
      data,
      origin: "push_tap",
    });

    handlePushNavigation(data);
  });

  LocalNotifications.addListener("localNotificationActionPerformed", (event) => {
    console.log("[LOCAL] Notifica aperta");

    const notification = event?.notification || {};
    const title = notification.title || "Stirup";
    const body = notification.body || "";
    const data = notification.extra || {};

    saveNotificationToHistory({
      title,
      body,
      data,
      origin: "local_tap",
    });

    handlePushNavigation(data);
  });
}

/* ===============================
   INIT PUSH
================================ */
export async function initPush() {
  if (!Capacitor.isNativePlatform()) {
    console.log("Push disabilitate: non è una piattaforma nativa");
    return;
  }

  if (initialized) return;

  console.log("🚀 INIT PUSH START");

  try {
    let permStatus = await FirebaseMessaging.checkPermissions();
    console.log("[PUSH] Permessi iniziali:", permStatus.receive === "granted" ? "granted" : "not_granted");

    if (permStatus.receive !== "granted") {
      permStatus = await FirebaseMessaging.requestPermissions();
      console.log("[PUSH] Permessi dopo request:", permStatus.receive === "granted" ? "granted" : "not_granted");
    }

    if (permStatus.receive !== "granted") {
      console.warn("Notifiche push non autorizzate");
      return;
    }

    try {
      const localPerm = await LocalNotifications.requestPermissions();
      console.log("[LOCAL] Permessi local notifications:", localPerm.display === "granted" ? "granted" : "not_granted");
    } catch (err) {
      console.warn("[LOCAL] requestPermissions fallita:", safeErrorDetails(err));
    }

    if (!listenersBound) {
      bindPushListeners();
      listenersBound = true;
    }

    await getAndStoreFcmToken();

    initialized = true;
  } catch (err) {
    console.error("❌ Errore initPush:", safeErrorDetails(err));
  }
}

/* ===============================
   LOGOUT
================================ */
const UNREGISTER_TIMEOUT_MS = 4000;

function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), ms)),
  ]);
}

/**
 * Scollega il token del dispositivo dall'utente che esce.
 * Best-effort: non rifiuta mai, così il logout non resta bloccato
 * (rete assente, JWT scaduto, Firebase non disponibile).
 * Il JWT va letto PRIMA che il logout lo rimuova: chiamare senza await
 * all'inizio del logout.
 */
export async function unregisterPushTokenOnLogout() {
  if (!Capacitor.isNativePlatform()) return;

  const jwt = localStorage.getItem("token");
  if (!jwt) return;

  try {
    let token = isValidFcmToken(lastPushToken) ? lastPushToken.trim() : "";
    if (!token) {
      const result = await withTimeout(FirebaseMessaging.getToken(), UNREGISTER_TIMEOUT_MS);
      token = result?.token?.trim?.() || "";
    }
    if (!isValidFcmToken(token)) return;

    await apiClient.post(
      "/api/push/unregister",
      { token },
      {
        headers: { Authorization: `Bearer ${jwt}` },
        timeout: UNREGISTER_TIMEOUT_MS,
      }
    );
    console.log("✅ Token push scollegato dall'utente");
  } catch (err) {
    console.warn("[PUSH] Unregister al logout non riuscito, logout prosegue:", safeErrorDetails(err));
  }
}

/* ===============================
   DOPO LOGIN
================================ */
export async function syncPushTokenAfterLogin() {
  if (!Capacitor.isNativePlatform()) return;

  const jwt = localStorage.getItem("token");
  if (!jwt) {
    console.warn("Nessun JWT trovato in syncPushTokenAfterLogin");
    return;
  }

  if (lastPushToken && isValidFcmToken(lastPushToken)) {
    await registerTokenIfPossible(lastPushToken);
    return;
  }

  await getAndStoreFcmToken();
}